import SelectMatchesDialogButton from "./Dialogs/SelectMatchesDialogButton";
import { useSelectedMatch } from "@/context/matchContext";
import { useSelectedTeam } from "@/context/teamContext";
import type { Tournament } from "@/api/tournaments";
import dayjs from "dayjs";
import { dateFormat, getTeamLogo } from "@/lib/utils";
import { Loader } from "../common/loader";
import { MatchKpis } from "@/api/ranking";
import { gameSummaryApi, gameSummaryQueryOptions } from "@/api/gameSummary";
import { GenerateSummaryButton } from "../common/GenerateSummaryButton";
import { SummaryDialog } from "../common/SummaryDialog";
import { useState, useEffect, useRef } from "react";
import { useLocation } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ONBOARDING_IDS } from "@/components/general/Onboarding/constants";

interface DashboardHeaderProps {
    kpiData?: MatchKpis;
    isKpisLoading?: boolean;
    tournaments?: Tournament[];
    isTournamentsLoading?: boolean;
}

export const DashboardHeader = ({
    kpiData,
    isKpisLoading = false,
    tournaments,
    isTournamentsLoading = false,
}: DashboardHeaderProps) => {
    const { tournamentName, matchIds, setMatchIds } = useSelectedMatch();
    const { setTeam } = useSelectedTeam();
    const [summaryDialog, setSummaryDialog] = useState<{
        isOpen: boolean;
        gameSummaryId: number;
        summary: string;
        matchId: number;
        gameInfo: { opponent: string; score: string; date: string };
    } | null>(null);
    const location = useLocation();
    const urlMatchId = new URLSearchParams(location.search).get("matchId");
    const autoOpenMatchId = urlMatchId ? parseInt(urlMatchId) : null;
    const autoOpenedRef = useRef<number | null>(null);

    const { data: autoSummary } = useQuery({
        ...gameSummaryQueryOptions(autoOpenMatchId ?? 0),
        enabled: !!autoOpenMatchId,
    });

    const isLoading = isTournamentsLoading || isKpisLoading;
    const initialAutoOpenRef = useRef(false);

    useEffect(() => {
        if (autoOpenMatchId && !initialAutoOpenRef.current) {
            initialAutoOpenRef.current = true;
            setTeam(527810, "Vålerenga");
            setMatchIds([autoOpenMatchId]);
        }
    }, [autoOpenMatchId, setTeam, setMatchIds]);

    const selectedMatches =
        tournaments?.flatMap((tournament) =>
            tournament.matches
                .filter((match) => matchIds.includes(match.id))
                .map((match) => ({ ...match, tournamentName: tournament.name }))
        ) || [];

    const fallbackMatch =
        selectedMatches.length === 0 && kpiData?.matches && kpiData.matches.length > 0
            ? tournaments
                  ?.find((tournament) => tournament.matches.some((match) => match.id === kpiData.matches[0].matchId))
                  ?.matches.find((match) => match.id === kpiData.matches[0].matchId)
            : null;

    const displayTournamentName =
        tournamentName ||
        (kpiData?.matches && kpiData.matches.length > 0 ? kpiData.matches[0].tournamentName : undefined);

    const isMultipleMatches = selectedMatches.length > 1;
    const displayMatch = selectedMatches.length === 1 ? selectedMatches[0] : fallbackMatch;

    const handleSummaryGenerated = (summaryId: number, summary: string) => {
        if (!displayMatch) return;
        const opponent =
            displayMatch.homeTeam.shortName === "Vålerenga"
                ? displayMatch.awayTeam.shortName
                : displayMatch.homeTeam.shortName;
        const score = `${displayMatch.homeGoals} - ${displayMatch.awayGoals}`;
        const date = dayjs(displayMatch.date).format(dateFormat);
        setSummaryDialog({
            isOpen: true,
            gameSummaryId: summaryId,
            summary,
            matchId: displayMatch.id,
            gameInfo: { opponent, score, date },
        });
    };

    const handleSummaryUpdated = (newSummary: string, newSummaryId: number) => {
        if (!summaryDialog) return;
        setSummaryDialog({ ...summaryDialog, summary: newSummary, gameSummaryId: newSummaryId });
    };

    useEffect(() => {
        if (!autoSummary || !autoOpenMatchId || !tournaments) return;
        if (autoOpenedRef.current === autoOpenMatchId) return;

        const matchDetails = tournaments.flatMap((t) => t.matches).find((m) => m.id === autoOpenMatchId);
        if (!matchDetails) return;

        autoOpenedRef.current = autoOpenMatchId;

        const opponent =
            matchDetails.homeTeam.shortName === "Vålerenga"
                ? matchDetails.awayTeam.shortName
                : matchDetails.homeTeam.shortName;
        const score = `${matchDetails.homeGoals} - ${matchDetails.awayGoals}`;
        const date = dayjs(matchDetails.date).format(dateFormat);

        setSummaryDialog({
            isOpen: true,
            gameSummaryId: autoSummary.id,
            summary: autoSummary.summary,
            matchId: autoOpenMatchId,
            gameInfo: { opponent, score, date },
        });
    }, [autoSummary, autoOpenMatchId, tournaments]);

    if (isLoading) {
        return (
            <div className="mb-6 flex items-center justify-center py-8">
                <Loader message="Loading match data..." className="text-sm" />
            </div>
        );
    }

    if (isMultipleMatches) {
        const uniqueTournaments = [...new Set(selectedMatches.map((m) => m.tournamentName))];
        return (
            <div className="mb-6 pb-2 md:pb-3 border-b border-outline-variant/30">
                <div className="flex flex-col items-start gap-1.5 md:flex-row md:items-center md:justify-between">
                    <div>
                        <h1 className="text-2xl font-headline font-bold text-on-surface">Multiple Matches Analysis</h1>
                        <p className="text-sm text-on-surface-variant mt-0.5">
                            {selectedMatches.length} matches
                            {uniqueTournaments.length > 1
                                ? ` across ${uniqueTournaments.length} tournaments`
                                : ` · ${uniqueTournaments[0]}`}
                        </p>
                    </div>
                    <div className="self-center md:self-auto mt-2 md:mt-0">
                        <SelectMatchesDialogButton />
                    </div>
                </div>
            </div>
        );
    }

    if (!displayMatch) {
        return (
            <div className="mb-6 flex items-center justify-between pb-3 border-b border-outline-variant/30">
                <div>
                    <h1 className="text-2xl font-headline font-bold text-on-surface-variant">No match selected</h1>
                    {displayTournamentName && (
                        <p className="text-sm text-on-surface-variant mt-0.5">{displayTournamentName}</p>
                    )}
                </div>
                <SelectMatchesDialogButton />
            </div>
        );
    }

    const homeLogo = getTeamLogo(displayMatch.homeTeam.fullName, "light") ?? null;
    const awayLogo = getTeamLogo(displayMatch.awayTeam.fullName, "dark") ?? null;
    const homeWin = displayMatch.homeGoals > displayMatch.awayGoals;
    const awayWin = displayMatch.awayGoals > displayMatch.homeGoals;

    return (
        <div id={ONBOARDING_IDS.DASHBOARD_HEADER} className="mb-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-4">
                <div className="pl-1.5 md:pl-0">
                    <h1 className="font-headline font-black text-2xl md:text-4xl text-primary leading-none uppercase tracking-tighter">
                        {displayMatch.homeTeam.shortName} vs {displayMatch.awayTeam.shortName}
                    </h1>
                    <p className="text-on-surface-variant text-sm mt-1">
                        {dayjs(displayMatch.date).format(dateFormat)}
                        {displayTournamentName && <> · {displayTournamentName}</>}
                    </p>
                </div>
                <div className="flex gap-2 flex-shrink-0 self-center md:self-auto">
                    <span id="onboarding-dashboard-ai-summary">
                        <GenerateSummaryButton
                            mutationFn={() => gameSummaryApi.generateSummary(displayMatch.id)}
                            onSummaryGenerated={handleSummaryGenerated}
                            variant="outlined"
                            size="md"
                        />
                    </span>
                    <span id="onboarding-dashboard-match-select">
                        <SelectMatchesDialogButton />
                    </span>
                </div>
            </div>

            <div className="relative">
                <div className="grid grid-cols-1 md:grid-cols-2 rounded-xl overflow-hidden shadow-[0_8px_32px_rgba(5,0,102,0.12)]">
                    <div className="bg-primary text-white p-4 md:p-6 lg:p-8 relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-3 opacity-10 pointer-events-none">
                            <span className="material-symbols-outlined" style={{ fontSize: 96 }}>
                                shield
                            </span>
                        </div>
                        <div className="relative z-10 flex flex-row items-center justify-between md:flex-col md:h-full md:items-start">
                            <div className="flex items-center gap-3 md:mb-6">
                                {homeLogo ? (
                                    <img
                                        src={homeLogo}
                                        alt={displayMatch.homeTeam.fullName}
                                        className="w-8 h-8 md:w-10 md:h-10 object-contain rounded-sm"
                                    />
                                ) : (
                                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-sm bg-white/10 flex items-center justify-center font-bold text-base md:text-lg">
                                        {displayMatch.homeTeam.shortName.charAt(0)}
                                    </div>
                                )}
                                <div className="flex flex-col-reverse md:flex-col gap-0.5">
                                    <span className="block text-[10px] tracking-[0.2em] opacity-60 font-bold uppercase">
                                        Home Team
                                    </span>
                                    <h2 className="font-headline font-extrabold text-xl md:text-2xl lg:text-3xl tracking-tight uppercase">
                                        {displayMatch.homeTeam.shortName}
                                    </h2>
                                </div>
                            </div>
                            <div className="md:mt-auto">
                                <div
                                    className={`font-headline font-black text-4xl md:text-7xl lg:text-8xl tracking-tighter leading-none mb-1 ${
                                        homeWin ? "text-white" : awayWin ? "text-white/50" : "text-white"
                                    }`}
                                    style={{ textShadow: "0 0 30px rgba(255,255,255,0.2)" }}
                                >
                                    {displayMatch.homeGoals}
                                </div>
                                <p className="hidden md:block text-[10px] tracking-widest uppercase text-blue-200 font-bold">
                                    Goals Scored
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-surface-container-lowest text-primary p-4 md:p-6 lg:p-8 relative overflow-hidden">
                        <div className="absolute top-0 left-0 p-3 opacity-5 pointer-events-none">
                            <span className="material-symbols-outlined" style={{ fontSize: 96 }}>
                                swords
                            </span>
                        </div>
                        <div className="relative z-10 flex flex-row-reverse items-center justify-between md:flex-col md:h-full md:items-end md:text-right">
                            <div className="flex flex-row-reverse items-center gap-3 md:mb-6">
                                {awayLogo ? (
                                    <img
                                        src={awayLogo}
                                        alt={displayMatch.awayTeam.fullName}
                                        className="w-8 h-8 md:w-10 md:h-10 object-contain rounded-sm opacity-90"
                                    />
                                ) : (
                                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-sm bg-surface-container flex items-center justify-center font-bold text-base md:text-lg text-on-surface-variant">
                                        {displayMatch.awayTeam.shortName.charAt(0)}
                                    </div>
                                )}
                                <div>
                                    <span className="block text-[10px] tracking-[0.2em] text-on-surface-variant font-bold uppercase mb-0.5 text-right">
                                        Challenger
                                    </span>
                                    <h2 className="font-headline font-extrabold text-xl md:text-2xl lg:text-3xl tracking-tight uppercase">
                                        {displayMatch.awayTeam.shortName}
                                    </h2>
                                </div>
                            </div>
                            <div className="md:mt-auto">
                                <div
                                    className={`font-headline font-black text-4xl md:text-7xl lg:text-8xl tracking-tighter leading-none mb-1 ${
                                        awayWin ? "text-primary" : homeWin ? "text-on-surface-variant" : "text-primary"
                                    }`}
                                >
                                    {displayMatch.awayGoals}
                                </div>
                                <p className="hidden md:block text-[10px] tracking-widest uppercase text-on-surface-variant font-bold">
                                    Goals Scored
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center z-10 pointer-events-none">
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#ba0a06] flex items-center justify-center text-center text-white font-black font-headline text-base md:text-lg shadow-xl ring-4 ring-surface">
                        VS
                    </div>
                </div>
            </div>

            {summaryDialog && (
                <SummaryDialog
                    isOpen={summaryDialog.isOpen}
                    onClose={() => setSummaryDialog(null)}
                    summaryId={summaryDialog.gameSummaryId}
                    summary={summaryDialog.summary}
                    entityType="game_summary"
                    title="AI Match Summary"
                    subtitle={
                        <>
                            <span className="font-medium">vs {summaryDialog.gameInfo.opponent}</span>
                            <span className="mx-2">&bull;</span>
                            <span>{summaryDialog.gameInfo.score}</span>
                            <span className="mx-2">&bull;</span>
                            <span>{summaryDialog.gameInfo.date}</span>
                        </>
                    }
                    regenerateFn={() => gameSummaryApi.regenerateSummary(summaryDialog.matchId)}
                    onSummaryUpdated={handleSummaryUpdated}
                />
            )}
        </div>
    );
};

export default DashboardHeader;
